"use client";

import { useContext, useEffect, useState } from "react";
import type { LandingPageTheme } from "@/lib/landing/puck/themes";
import {
  landingThemeDataAttributes,
  landingThemeInlineStyle,
  resolveLandingColorSchemeDark,
} from "@/lib/landing/puck/themes";
import { LandingThemeInitialDarkContext } from "@/lib/landing/puck/theme-context";

type LandingCmsThemeProps = {
  theme: LandingPageTheme;
  children: React.ReactNode;
  initialDark?: boolean;
};

export function LandingCmsTheme({
  theme,
  children,
  initialDark,
}: LandingCmsThemeProps) {
  const initialFromContext = useContext(LandingThemeInitialDarkContext);
  const seedDark = initialDark ?? initialFromContext;
  const [resolvedDark, setResolvedDark] = useState(() =>
    resolveLandingColorSchemeDark(theme, seedDark),
  );

  useEffect(() => {
    const update = () => {
      setResolvedDark(resolveLandingColorSchemeDark(theme));
    };
    update();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", update);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => {
      mq.removeEventListener("change", update);
      observer.disconnect();
    };
  }, [theme]);

  const forcedScheme =
    theme.colorScheme === "auto"
      ? undefined
      : theme.colorScheme === "dark"
        ? "dark"
        : "light";

  return (
    <div
      className="landing-cms-theme min-h-screen bg-body text-primary"
      data-theme={forcedScheme}
      {...landingThemeDataAttributes(theme, resolvedDark)}
      style={landingThemeInlineStyle(theme, resolvedDark)}
    >
      {children}
    </div>
  );
}
